import Core            from '@/core/Core';
import Dictionary      from '@/core/types/Dictionary';
import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

import SupplyOffer    from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOffersDAO from '@/domains/SupplyOffers/SupplyOffersDAO';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import type SupplyOfferStatus from '@/domains/SupplyOffers/SupplyOfferStatus';

class SupplyOffersService {

  /** Срок актуальности цены в днях */
  public static PRICE_EXPIRATION_DAYS = 365;

  // ============================================================
  // Кеш статусов
  // ============================================================

  /**
   * Статусы разрешения. Наполняются из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static statuses = reactive(new Dictionary<string, SupplyOfferStatus>());

  /**
   * Кеш статусов доступности. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static statusesAvailability = reactive(new Dictionary<string, SupplyOfferStatus>());

  /** Порядок отображения статусов: одобрен → в процессе → отклонён */
  private static readonly statusesOrder = ['02_approved', '01_testing', '03_declined'];

  /** Статусы разрешения в порядке отображения */
  static getStatuses(): SupplyOfferStatus[] {
    return this.statuses.toArray().sort((a, b) => {
      return this.statusesOrder.indexOf(a.id) - this.statusesOrder.indexOf(b.id);
    });
  }

  /** Статусы доступности */
  static getStatusesAvailability(): SupplyOfferStatus[] {
    return this.statusesAvailability.toArray();
  }

  /** Статус по умолчанию для нового разрешения */
  static getDefaultStatus(): SupplyOfferStatus | null {
    return this.getStatuses()[0] ?? null;
  }

  /** Статус доступности по умолчанию для нового разрешения */
  static getDefaultStatusAvailability(): SupplyOfferStatus | null {
    return this.statusesAvailability.find(_status => _status.id === 'available') ?? null;
  }

  // ============================================================
  // CRUD
  // ============================================================

  /** Загрузить одно разрешение по id */
  static async get(id: number): Promise<SupplyOffer | null> {
    const response = await SupplyOffersDAO.get(id);
    return Core.utils.hydrate(response.data, SupplyOffer.fromServer);
  }

  /**
   * Загрузить список разрешений с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getListPaginated(
    params?: Parameters<typeof SupplyOffersDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: SupplyOffer[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await SupplyOffersDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, SupplyOffer.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * То же, что getListPaginated, но с actual-тарифами.
   * Реестр ходит сюда, потому что показывает цены.
   */
  static async getListWithActualTariffsPaginated(
    params?: Parameters<typeof SupplyOffersDAO.getListWithActualTariffs>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: SupplyOffer[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await SupplyOffersDAO.getListWithActualTariffs(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, SupplyOffer.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загрузить список разрешений.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof SupplyOffersDAO.getList>[0]
  ): Promise<SupplyOffer[]> {
    return (await this.getListPaginated(params)).data;
  }

  /** Создать разрешение */
  static async add(supplyOffer: SupplyOffer): Promise<SupplyOffer | null> {
    const response = await SupplyOffersDAO.add(supplyOffer);

    if (response.ok && response.data) {
      Core.showNotification.success('Разрешение на поставку добавлено');
      return Core.utils.hydrate(response.data, SupplyOffer.fromServer);
    }
    else {
      Core.showNotification.error('Ошибка при создании разрешения');
      return null;
    }
  }

  /** Обновить разрешение */
  static async update(supplyOffer: SupplyOffer): Promise<boolean> {
    const response = await SupplyOffersDAO.update(supplyOffer);

    if (response.ok) {
      Core.showNotification.success('Разрешение на поставку обновлено');
    }
    else {
      Core.showNotification.error('Ошибка при обновлении разрешения');
    }

    return response.ok;
  }

  /** Удалить разрешение с указанием причины */
  static async delete(supplyOffer: SupplyOffer, reasonText: string): Promise<boolean> {
    const response = await SupplyOffersDAO.delete(supplyOffer, reasonText);

    if (response.ok) {
      Core.showNotification.success('Разрешение удалено');
    }
    else {
      Core.showNotification.error('Ошибка при удалении разрешения');
    }

    return response.ok;
  }

  /**
   * Удалить разрешение с подтверждением.
   * Возвращает true если удаление произошло — вызывающий сам решает что делать
   * (обычно refresh реестра).
   */
  static async deleteWithConfirm(supplyOffer: SupplyOffer): Promise<boolean> {
    const { isApproved, reasonText } = await Core.dialogs.confirmWithReason(
      `Удалить поставщика "${supplyOffer.seller!.name}" из подтвержденных?`,
      `Удаление одобренного поставщика`,
    );

    if (isApproved) {
      return await this.delete(supplyOffer, reasonText);
    }
    else {
      return false;
    }
  }

  // ============================================================
  // Диалоги
  // ============================================================

  /**
   * Показывает карточку разрешения на поставку.
   * Принимает готовое разрешение (из реестра — для редактирования,
   * либо свежесозданное SupplyOffer.fromSellerProduct(...) — для создания).
   */
  static showSupplyOfferCard(supplyOffer: SupplyOffer): Promise<boolean> {
    return Core.dialogs.SupplyOfferCard.showAsPromise({ supplyOffer });
  }

  static showStatusPopup(supplyOffer: SupplyOffer): Promise<boolean> {
    Core.dialogs.SupplyOfferStatusPopup.hideIfOpen();
    return Core.dialogs.SupplyOfferStatusPopup.showAsPromise({ supplyOffer });
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (supplyOffer: SupplyOffer) => { return SupplyOffersService.showSupplyOfferCard(supplyOffer); },
        null,
        false,
        true,
        true,
      ),
      new ContextMenuItem(
        'Изменить статус',
        'swap_vert',
        (supplyOffer: SupplyOffer) => { return SupplyOffersService.showStatusPopup(supplyOffer); },
        null,
        false,
        true,
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (supplyOffer: SupplyOffer) => { return SupplyOffersService.deleteWithConfirm(supplyOffer); },
        null,
        false,
        true,
      ),
    ];
  }
}

export default SupplyOffersService;
